import * as yup from 'yup';
import { DocumentNode } from 'graphql';
import { useForm } from 'react-form-state-manager';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { GetOrderQuery as GetOrder } from '__generated__/graphql';
import { filterPrefix, getServerErrors } from '../../../../common/helpers';
import { useNotifier } from '../../../../common/Notifications/NotificationProvider';
import ContactPicker, { Participant } from '../../Contacts/ContactPicker';
import EditOrderFromBackendMutation from './EditOrderFromBackendMutation';
import GetOrderQuery from './GetOrderQuery';
import InvoiceDetailsForm, { getInvoiceSchema } from '../../../../common/Invoice/InvoiceDetailsForm';
import UI from '../../../../common/UI';
import useMutation from '../../../../api/useMutation';
import useProject from '../../../useProject';
import useScroll from '../../../../common/useScroll';
import useValidation from '../../../../common/useValidation';

type Order = GetOrder['order'];

export interface EditInvoiceFormProps {
  order: Order;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

const EditInvoiceForm = ({ order, onSuccess, onCancel, refetchQueries = [GetOrderQuery] }: EditInvoiceFormProps) => {
  const { t } = useTranslation();
  const project = useProject();
  const notifier = useNotifier();
  const { scrollToIfInvisible } = useScroll();

  const getValues = (order: Order) => ({
    participant: {
      id: order.participant.id,
    },
    invoice: {
      company_name: order.company_name,
      is_charity: order.is_charity,
      reference: order.reference,
      vat_id: order.vat_id,
      street: order.street,
      house_number: order.house_number,
      zip_code: order.zip_code,
      city: order.city,
      extra_address_line: order.extra_address_line,
      country: order.country || project.organisation_country,
      phone: order.phone,
    },
    unset_invoice_details: false,
  });

  const form = useForm({
    values: getValues(order),
  });

  const invoiceDetails = !form.values.unset_invoice_details ? {
    ...form.values.invoice,
    // Do not send VAT ID if it was not changed, so that it is not validated by the server.
    vat_id: order.vat_id_editable ? form.values.invoice.vat_id : null,
  } : null;

  const [editOrder, { error, loading }] = useMutation(
    EditOrderFromBackendMutation,
    {
      variables: {
        input: {
          id: order.id,
          participant: {
            id: form.values.participant.id,
          },
          invoice: !form.values.unset_invoice_details ? {
            ...form.values.invoice,
            // Do not send VAT ID if it was not changed, so that it is not validated by the server.
            vat_id: order.vat_id_editable ? form.values.invoice.vat_id : null,
          } : null,
          unset_invoice_details: form.values.unset_invoice_details,
        },
      },
      refetchQueries,
      awaitRefetchQueries: true,
      onCompleted: () => {
        notifier.success(t('notifications.order_edited'));
        onSuccess?.();
      },
      onError: () => {
        scrollToIfInvisible('EditInvoiceForm');
      },
    },
  );

  const [participant, setParticipant] = useState<Participant>({
    id: order.participant.id,
    full_name: `${order.participant.first_name} ${order.participant.last_name}`,
    email: order.participant.email,
  });

  const handleChangeParticipant = (participant: Participant) => {
    form.set('participant.id', participant.id);
    setParticipant(participant);
  };

  const { errors, valid } = useValidation({
    schema: yup.object({
      invoice: getInvoiceSchema().nullable(),
    }),
    values: {
      invoice: invoiceDetails,
    },
    externalErrors: getServerErrors(error),
  });

  return (
    <UI.Form onSubmit={() => editOrder()} id="EditInvoiceForm">
      <UI.FormGrid>
        <UI.Legend>
          {t('edit_invoice_details')}
        </UI.Legend>

        <UI.ServerErrors error={error} />

        <UI.InputGroup>
          <UI.InputLabel sc={{ mb: 0 }}>
            {t('contact')}
          </UI.InputLabel>
          <ContactPicker
            participant={participant}
            onChange={(participant) => handleChangeParticipant(participant)}
            nullable={false}
          />
        </UI.InputGroup>

        <UI.InputGroup>
          <UI.Checkbox
            checked={!form.values.unset_invoice_details}
            onChange={() => form.set('unset_invoice_details', (value: boolean) => !value)}
            name="specify_details"
          >
            {t('specify_invoice_details')}
          </UI.Checkbox>
        </UI.InputGroup>

        {!form.values.unset_invoice_details && (
          <InvoiceDetailsForm
            value={form.values.invoice}
            onChange={(value) => form.set((input) => ({ ...input, invoice: { ...input.invoice, ...value } }))}
            onBlur={(field) => form.setTouched(`invoice.${field}`, true)}
            touched={form.touched.invoice}
            errors={filterPrefix(errors, 'invoice')}
            disableVatId={!order.vat_id_editable}
          />
        )}

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save')}
          loading={loading}
          valid={valid}
          onClose={onCancel}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditInvoiceForm;
